import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  fetchMaintenanceMasters,
  fetchMaintenanceRequests,
  fetchMaintenanceMetrics,
} from "../api/maintenanceApi";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import { useMaintenanceRole } from "../utils/useMaintenanceRole";
import { MAINTENANCE_RECORDS_SUBMODULE_SLUG } from "../utils/maintenancePermissionSlugs";
import MaintenanceRequestModalsSection from "../components/control/sections/MaintenanceRequestModalsSection";
import OfficeMaintenanceDashboardMetricsSection from "../components/office/sections/OfficeMaintenanceDashboardMetricsSection";
import OfficeMaintenanceCreateSection from "../components/office/sections/OfficeMaintenanceCreateSection";
import OfficeMaintenanceDirectorySection, {
  MAINTENANCE_DIRECTORY_COLUMN_DEFS,
  createOfficeMaintenanceInitialColumnVisibility,
  maintenanceDefaultDirectoryFilters,
  maintenanceDirectoryCellValue,
} from "../components/office/sections/OfficeMaintenanceDirectorySection";
import OfficeMaintenanceQuickJumpSection from "../components/office/sections/OfficeMaintenanceQuickJumpSection";
import OfficeMaintenanceRequestsPanel from "../components/office/sections/OfficeMaintenanceRequestsPanel";
import OfficeMaintenanceStatusTrackerSection from "../components/office/sections/OfficeMaintenanceStatusTrackerSection";

import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";

const DIRECTORY_PAGE_SIZE = 10;
const TRACKER_PAGE_SIZE = 20;

const EMPTY_MAINTENANCE_TYPES = [];
const EMPTY_MAINTENANCE_STATUSES = [];

const EMPTY_METRICS = {
  total_requests: 0,
  pending_requests: 0,
  in_progress_requests: 0,
  completed_requests: 0,
  total_cost: 0,
};

function safeErrorText(err, fallback = "Something went wrong. Please try again.") {
  const m = err?.message;
  return typeof m === "string" && m.trim() ? m : fallback;
}

const QUICK_JUMP_BASE = [
  { key: "stats", label: "Performance overview", targetId: "mr-stats" },
  { key: "create", label: "New request", targetId: "mr-create" },
  { key: "workspace", label: "Requests", targetId: "mr-workspace" },
];

function filterQuickJump(items, { showSummary, showCreate, showWorkspace }) {
  return items.filter((item) => {
    if (item.key === "stats") return showSummary;
    if (item.key === "create") return showCreate;
    if (item.key === "workspace") return showWorkspace;
    return true;
  });
}

const MaintenanceRecordsPage = () => {
  const perms = useMaintenanceRole(MAINTENANCE_RECORDS_SUBMODULE_SLUG);
  const showSummary = perms.canView;
  const showCreate = perms.canCreate;
  const showDirectory = perms.canView;
  const hasAnyAccess = showSummary || showCreate || showDirectory;
  const [searchParams, setSearchParams] = useSearchParams();
  const workspaceTab = searchParams.get("tab") === "tracker" ? "tracker" : "directory";

  const setWorkspaceTab = useCallback(
    (tab) => {
      if (tab === "tracker") setSearchParams({ tab: "tracker" }, { replace: true });
      else setSearchParams({}, { replace: true });
    },
    [setSearchParams],
  );

  const [masters, setMasters] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [metrics, setMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [directoryPage, setDirectoryPage] = useState(1);
  const [directoryTotal, setDirectoryTotal] = useState(0);
  const [directoryTotalPages, setDirectoryTotalPages] = useState(1);
  const [draftDirectoryFilters, setDraftDirectoryFilters] = useState(() =>
    maintenanceDefaultDirectoryFilters(),
  );
  const [appliedDirectoryFilters, setAppliedDirectoryFilters] = useState(() =>
    maintenanceDefaultDirectoryFilters(),
  );
  const [columnVisibility, setColumnVisibility] = useState(
    createOfficeMaintenanceInitialColumnVisibility,
  );
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [activeQuickJump, setActiveQuickJump] = useState(null);

  const [trackerRows, setTrackerRows] = useState([]);
  const [trackerPage, setTrackerPage] = useState(1);
  const [trackerTotal, setTrackerTotal] = useState(0);
  const [trackerTotalPages, setTrackerTotalPages] = useState(1);
  const [trackerLoading, setTrackerLoading] = useState(false);
  const [trackerStatusKey, setTrackerStatusKey] = useState("all");

  const [showViewForm, setShowViewForm] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const maintenanceTypes = masters?.maintenanceTypes ?? EMPTY_MAINTENANCE_TYPES;
  const maintenanceStatuses =
    masters?.maintenanceStatuses ?? EMPTY_MAINTENANCE_STATUSES;

  const onNotify = useCallback((msg, opts) => {
    if (!msg) {
      setErrorMsg("");
      setSuccessMsg("");
      return;
    }
    if (opts?.variant === "error") {
      setErrorMsg(msg);
      return;
    }
    setSuccessMsg(msg);
  }, []);

  const refreshMetrics = useCallback(async () => {
    try {
      const data = await fetchMaintenanceMetrics();
      setMetrics(data);
    } catch {
      setMetrics({ ...EMPTY_METRICS });
    }
  }, []);

  const dirUserSearch = appliedDirectoryFilters.user_search;
  const dirColumn = appliedDirectoryFilters.column;
  const dirValue = appliedDirectoryFilters.value;

  const directoryApiFilters = useMemo(() => {
    const q = {};
    const name = String(dirUserSearch || "").trim();
    if (name) q.name = name;

    const col = dirColumn;
    const val = String(dirValue || "").trim();
    if (!col || !val) return q;

    if (col === "id") {
      const n = parseInt(String(val).replace(/\D/g, ""), 10);
      if (Number.isFinite(n)) q.request_id = String(n);
    }
    if (col === "created_at") {
      q.created_date = val.slice(0, 10);
    }
    if (col === "type") {
      const t = maintenanceTypes.find((x) => String(x.type_name) === val);
      if (t?.id != null) q.type_id = String(t.id);
    }
    if (col === "status") {
      const s = maintenanceStatuses.find((x) => String(x.status_name) === val);
      if (s?.id != null) q.status_id = String(s.id);
    }
    if (col === "asset_area") q.asset_area = val;
    if (col === "issue_description") q.issue_description = val;
    if (col === "reported_by") {
      const id = parseInt(String(val).trim(), 10);
      if (Number.isFinite(id)) q.reported_by_id = String(id);
    }
    if (col === "assigned_to_name") q.assigned_name_contains = val;
    if (col === "cost_incurred") {
      const n = Number(String(val).replace(/[^\d.-]/g, ""));
      if (Number.isFinite(n)) q.cost_match = String(n);
    }
    return q;
  }, [dirUserSearch, dirColumn, dirValue, maintenanceTypes, maintenanceStatuses]);

  const fetchRequestsList = useCallback(async () => {
    setLoadingRequests(true);
    setErrorMsg("");
    try {
      const result = await fetchMaintenanceRequests(
        directoryPage,
        DIRECTORY_PAGE_SIZE,
        directoryApiFilters,
      );
      setRequests(result.rows);
      setDirectoryTotal(result.total);
      setDirectoryTotalPages(result.totalPages);
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    } finally {
      setLoadingRequests(false);
    }
  }, [directoryPage, directoryApiFilters]);

  const fetchTrackerList = useCallback(async () => {
    setTrackerLoading(true);
    try {
      const filters = {};
      if (trackerStatusKey !== "all") {
        const sid = parseInt(trackerStatusKey, 10);
        if (Number.isFinite(sid)) filters.status_id = String(sid);
      }
      const result = await fetchMaintenanceRequests(trackerPage, TRACKER_PAGE_SIZE, filters);
      setTrackerRows(result.rows);
      setTrackerTotal(result.total);
      setTrackerTotalPages(result.totalPages);
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    } finally {
      setTrackerLoading(false);
    }
  }, [trackerPage, trackerStatusKey]);

  const getMaintenanceExportData = useCallback(() => {
    const visibleCols = MAINTENANCE_DIRECTORY_COLUMN_DEFS.filter(
      (c) => columnVisibility[c.id],
    );
    const headers = visibleCols.map((c) => c.label);
    const rows = requests.map((r) =>
      visibleCols.map((c) => maintenanceDirectoryCellValue(c.id, r, masters)),
    );
    return { headers, rows };
  }, [requests, columnVisibility, masters]);

  useEffect(() => {
    if (perms.permissionsLoading || !showSummary) return;
    let cancelled = false;
    (async () => {
      setLoadError("");
      try {
        const mData = await fetchMaintenanceMasters();
        if (!cancelled) setMasters(mData);
      } catch (e) {
        if (!cancelled) setLoadError(e.message || "Could not load data.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [perms.permissionsLoading, showSummary]);

  useEffect(() => {
    if (perms.permissionsLoading || !showSummary) return;
    let cancelled = false;
    (async () => {
      setLoadingMetrics(true);
      try {
        const met = await fetchMaintenanceMetrics();
        if (!cancelled) setMetrics(met);
      } catch {
        if (!cancelled) setMetrics({ ...EMPTY_METRICS });
      } finally {
        if (!cancelled) setLoadingMetrics(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [perms.permissionsLoading, showSummary]);

  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedDirectoryFilters({ ...draftDirectoryFilters });
      setDirectoryPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [draftDirectoryFilters]);

  useEffect(() => {
    if (perms.permissionsLoading || !showDirectory) return;
    fetchRequestsList();
  }, [fetchRequestsList, perms.permissionsLoading, showDirectory]);

  useEffect(() => {
    if (perms.permissionsLoading || !showDirectory || workspaceTab !== "tracker") return;
    fetchTrackerList();
  }, [workspaceTab, fetchTrackerList, perms.permissionsLoading, showDirectory]);

  const handleRequestCreated = useCallback(() => {
    refreshMetrics();
    fetchRequestsList();
    fetchTrackerList();
  }, [refreshMetrics, fetchRequestsList, fetchTrackerList]);

  const applyTrackerStatusFilter = useCallback((key) => {
    setTrackerStatusKey(key);
    setTrackerPage(1);
  }, []);

  const handleViewForm = useCallback((request) => {
    setSelectedRequest(request);
    setShowViewForm(true);
  }, []);

  const handleCloseViewForm = useCallback(() => {
    setShowViewForm(false);
    setSelectedRequest(null);
  }, []);

  const handleRequestUpdated = useCallback(() => {
    refreshMetrics();
    fetchRequestsList();
    fetchTrackerList();
  }, [refreshMetrics, fetchRequestsList, fetchTrackerList]);

  const noop = useCallback(() => {}, []);

  const quickJumpItems = useMemo(
    () =>
      filterQuickJump(QUICK_JUMP_BASE, {
        showSummary,
        showCreate,
        showWorkspace: showDirectory,
      }),
    [showSummary, showCreate, showDirectory],
  );

  const normalizedLoadError = loadError.trim().toLowerCase();
  const normalizedErrorMsg = errorMsg.trim().toLowerCase();
  const showLoadErrorBanner = Boolean(
    loadError && (!errorMsg || normalizedLoadError !== normalizedErrorMsg),
  );

  return (
    <div className="user-builder-shell">
      <header className="user-builder-hero">
        <div className="user-builder-hero__text">
          <p className="user-builder-hero__eyebrow">Office administration</p>
          <h2 className="user-builder-hero__title">Maintenance records</h2>
          <p className="user-builder-hero__sub">
            Log requests, scan performance metrics, then manage everything under Requests—search and
            export in the directory, or follow status and dates in the tracker.
          </p>
        </div>
      </header>

      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalFeedback type="error" text={errorMsg} />
      <GlobalFeedback type="error" text={perms.permissionsError} role="alert" />
      {showLoadErrorBanner ? <GlobalFeedback type="error" text={loadError} /> : null}
      {perms.permissionsLoading ? (
        <p className="schedule-table-loading" style={{ padding: "2rem 1.5rem" }}>
          Loading permissions…
        </p>
      ) : null}
      {!perms.permissionsLoading && !hasAnyAccess ? (
        <GlobalFeedback
          type="warning"
          text="No permission for maintenance records. Enable View and/or Create on maintenance-records in User builder for this role, then sign in again."
          role="status"
        />
      ) : null}

      <div className="user-builder-layout">
        <div className="user-builder-layout__main">
          {!perms.permissionsLoading && showSummary ? (
            <OfficeMaintenanceDashboardMetricsSection
              metrics={metrics}
              loadingMetrics={loadingMetrics}
            />
          ) : null}

          {!perms.permissionsLoading && showCreate ? (
            <OfficeMaintenanceCreateSection
              mastersFromParent
              masters={masters}
              mastersLoadError={loadError}
              onNotify={onNotify}
              onRequestCreated={handleRequestCreated}
            />
          ) : null}

          {!perms.permissionsLoading && showDirectory ? (
            <OfficeMaintenanceRequestsPanel
              activeTab={workspaceTab}
              onTabChange={setWorkspaceTab}
              directorySlot={
                <OfficeMaintenanceDirectorySection
                  embedded
                  requests={requests}
                  masters={masters}
                  draftDirectoryFilters={draftDirectoryFilters}
                  onDraftDirectoryFiltersChange={setDraftDirectoryFilters}
                  onClearDirectoryFilters={() =>
                    setDraftDirectoryFilters(maintenanceDefaultDirectoryFilters())
                  }
                  columnVisibility={columnVisibility}
                  onColumnVisibilityChange={setColumnVisibility}
                  getMaintenanceExportData={
                    perms.canExport ? getMaintenanceExportData : undefined
                  }
                  loadingRequests={loadingRequests}
                  directoryPage={directoryPage}
                  directoryTotalPages={directoryTotalPages}
                  directoryTotal={directoryTotal}
                  directoryPageSize={DIRECTORY_PAGE_SIZE}
                  onDirectoryPageChange={setDirectoryPage}
                  onView={handleViewForm}
                  onEdit={undefined}
                  onAssign={undefined}
                  onStatus={undefined}
                  onDelete={undefined}
                />
              }
              trackerSlot={
                <OfficeMaintenanceStatusTrackerSection
                  masters={masters}
                  rows={trackerRows}
                  loading={trackerLoading}
                  page={trackerPage}
                  pageSize={TRACKER_PAGE_SIZE}
                  total={trackerTotal}
                  totalPages={trackerTotalPages}
                  onPageChange={setTrackerPage}
                  onViewRequest={handleViewForm}
                  activeStatusKey={trackerStatusKey}
                  onStatusFilterAll={() => applyTrackerStatusFilter("all")}
                  onStatusFilterChange={(key) => applyTrackerStatusFilter(key)}
                />
              }
            />
          ) : null}
        </div>

        {quickJumpItems.length > 0 ? (
          <OfficeMaintenanceQuickJumpSection
            asideAriaLabel="Maintenance records sections"
            items={quickJumpItems}
            activeKey={activeQuickJump}
            onActiveChange={setActiveQuickJump}
          />
        ) : null}
      </div>

      <MaintenanceRequestModalsSection
        masters={masters}
        onNotify={onNotify}
        showViewForm={showViewForm}
        selectedRequest={selectedRequest}
        onCloseViewForm={handleCloseViewForm}
        showAssignForm={false}
        onCloseAssignForm={noop}
        selectedRequestForAssign={null}
        setSelectedRequestForAssign={noop}
        onRequestUpdated={handleRequestUpdated}
      />
    </div>
  );
};

export default MaintenanceRecordsPage;
